declare module 'react-router-dom' {
  // Override the useLocation hook so that its returned location's state is generic.
  export function useLocation<S = unknown>(): Omit<Location, 'state'> & {state: S};
}

export interface Identity {
  readonly id: number | string | undefined;
}

export interface NamedItem extends Identity {
  name: string;
}

export interface NamedImageItem extends Identity {
  name: string;
  image_url: string;
}

export interface Brand extends NamedItem {
  product_count: number | undefined;
  review_count: number | undefined;
  avg_rating: number | undefined;
  avg_ranking: number | undefined;
}

export interface Ranking extends Identity {
  rank: number;
  keyword_value: string;
  created_on: string;
  updated_on: string;
}

export interface Product extends Identity {
  upc: string;
  name: string;
  size: string;
  description: string;
  brand: NamedItem;
}

/** ### The 'items' in Launch/Analysis/Items
 * Some fields are known, but most depending on what
 * the customer has loaded.
 */
export type ProductExpando = Partial<Product> & Record<string & {}, any>;

export interface Retailer extends NamedItem {
  retailer_code?: string;
  is_scrapable: boolean;
  is_regional: boolean;
  image_url: string;
}

export interface InitData {
  sale_price: number;
  rank: number;
  number_of_reviews: number;
  rating: number;
}

export interface RetailerProductData extends Identity {
  data: InitData;
}

export interface RetailerProduct extends Identity {
  price: string;
  rating: string;
  review_count: number;
  retailer: number;
  product: Product;
  search_url: string;
  product_url: string;
  image_url: string;
  badges: string[];
  ranking: Ranking;
  data: RetailerProductData;
  retailer_number: number;
}

export interface Store extends Identity {
  number: number;
  name: string;
  phone: string;
  latitude: number;
  longitude: number;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  retailer: string;
}

export interface CampaignItem extends Identity {
  campaign_id: string;
  path_id: string;
  campaign_name: string;
  brand: NamedItem;
  client_name: string;
  start_date: string;
  end_date: string;
  retailer: NamedItem[];
  keywords: string[];
  packages: string[];
  upc_list: number[];
  keyword_jobs: KeywordJob[];
}

// Mirrors the `region` block on the campaign API response, which is derived from the
// campaign's stores rather than stored on it. Null when the campaign has no stores, spans
// more than one country, or its stores cannot be resolved to a country.
export interface Region {
  code: string;
  name: string;
  currency_code: string;
  tc_geo_unit_label: string;
}

export interface SalesDataset extends Identity {
  slug: string;
  name: string;
  description: string;
  retailer: Retailer | null;
  regional: boolean;
  own_campaign_id: number;
  week_ending_day_of_week: 0 | 1 | 2 | 3 | 4 | 5 | 6 | null;
  allow_week_ending_day_of_week_adjustment: boolean;
  import_only_confirmed_locations: boolean;
}

export type ReportTemplate = 'base' | 'chicory' | 'inmarket' | 'groundtruth';
export type ReportTemplateOverride = '' | ReportTemplate;

export const REPORT_TEMPLATE_OPTIONS: {value: ReportTemplate; label: string}[] = [
  {value: 'base', label: 'PathX'},
  {value: 'chicory', label: 'Chicory'},
  {value: 'inmarket', label: 'InMarket'},
  {value: 'groundtruth', label: 'GroundTruth'},
];

export interface Campaign extends Identity {
  campaign_name: string;
  path_id: string;
  form_retailers: string;
  budget: string;
  impressions: string;
  brand: string;
  start_date: string;
  end_date: string;
  goal_of_campaign: string;
  service_option: string;
  urgent: string;
  form_contact: string;
  client_contact?: ClientContact;
  campaign_description: string;
  mondays_item_id: string;
  hubspot_deal_id?: string | null;
  retailer_name: string;
  retailer_external_code: string;
  main_campaign: boolean;
  auto_include_zip3_lift: boolean;
  store_id_mode: string;
  pre_period_start_date: string;
  pre_period_end_date: string;
  test_control_due_date?: string;
  mid_campaign_report_due_date?: string;
  first_in_campaign_report_date?: string | null;
  final_deliverable_due_date?: string;
  user_access: string;
  creator: any;
  client: any;
  media_partner: any;
  success_manager_user: any;
  sales_user: any;
  regional: boolean;
  region: Region | null;
  preferences: any;
  test_control_selection_id: number | null;
  test_control_findings_request_id?: number | null;
  test_control_data: any;
  brands: any;
  category: DashboardCategory | null;
  is_sales_partition_present: boolean;
  data_flags: {
    is_per_store_sales_calculation_required: boolean;
    is_per_store_sales_rollup_calculation_required: boolean;
    is_per_item_sales_calculation_required: boolean;
    is_sales_metrics_valid: boolean;
    is_store_metrics_valid: boolean;
    is_test_control_selected: boolean;
    is_upc_metrics_valid: boolean;
    is_sales_lift_analysis_run_required: boolean;
  };
  campaign_valassis_config_id: number | null;
  num_post_campaign_weeks: number;
  sales_dataset: SalesDataset | null;
  own_sales_dataset: SalesDataset;
  retailer_image: string;
  client_image: string;
  report_template_override: ReportTemplateOverride;
  report_template: ReportTemplate;
  client_default_report_template: ReportTemplate;
  permission_codes: string[];
  published_campaign_analysis_run?: number | string;
  customer_io: string;
  file_request?: {url?: string};
  campaign_export_notes?: string;
  locked: boolean;
  locks: string[];
  po_dollars?: number;
}

export type CampaignHealthCode =
  | 'rfp_submitted'
  | 'communicated_with_customer'
  | 'initial_sales_uploaded'
  | 'confirmed_campaign_details'
  | 'item_groups_selected'
  | 'test_control_selected'
  | 'weekly_sales_uploaded'
  | 'sales_lift_analysis_run'
  | 'campaign_region_consistent';

export interface CampaignAddHealth extends Campaign {
  health_checklist_items: {
    stage: 'green' | 'gray' | 'yellow' | 'red';
    health_checklist_item: {
      position: number;
      name: string;
      code: CampaignHealthCode;
    };
  }[];
}

export interface ClientContact extends Identity {
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  name: string;
}

export interface Profile extends Identity {
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  is_staff: boolean;
}

export interface KeywordJob {
  id: number;
  start_date: string;
  end_date?: string;
  retailer_id: number;
  error: string;
  success: boolean;
}

export interface InventoryItemList extends NamedItem {
  campaign_id: number;
  campaign_name: string;
  campaign_pathid: string;
  retailer_name: string;
}

export interface State extends Identity {
  title: string;
  short_title: string;
  country_code: string;
}

export type ForeignId = number | string | undefined;

export type CampaignFileUploadType = 'items' | 'stores' | 'areas';

export type UploadedFileDataType = 'sales' | 'pre_sales_or_rfp' | 'rfp' | 'media';

export type AnalysisTabName = 'locations' | 'items' | 'sales' | 'map';

export type AnalysisModal = 'select-test-control';

export type UploadedFile = {
  id: number;
  campaign: number;
  status: string;
  original_filename: string;
  user: number;
  file_size: number;
  file_hash: string;
  upload_date: any;
  file_adapter_status: any;
  details: any;
  file_adapter: any;
};

export type CampaignActionAlerts = {
  /** Campaign ID */
  id: number;
  path_id: string;
  campaign_name: string;
  health_alerts: ActionAlert[];
};

export type ActionAlert = {
  severity: 'red' | 'yellow' | 'gray';
  health_alert: {
    code: string;
    name: string;
  };
  health_checklist_item: {
    name: string;
    code: CampaignHealthCode;
    position: number;
  };
};

export interface ListResponse<T> {
  count: number;
  results: T[];
}

export interface ListLoaded<T> extends ListResponse<T> {
  as: 'success';
}

export interface ListData<T> extends ListResponse<T> {
  loading: boolean;
}

export type ListResult<T> =
  {as: 'idle'} | {as: 'loading'; requestId: string} | {as: 'error'; error: object} | ListLoaded<T>;
export type SingleResult<T> = {as: 'idle'} | {as: 'loading'} | {as: 'error'; error: object} | {as: 'success'; value: T};
export type AsyncResult<T> = SingleResult<T> & {invokeId?: number; previous?: T};

export type HomeStat = {
  total_campaigns: number;
  completed_campaigns: number;
  in_progress_campaigns: number;
  total_rfps: number;
};

export type CampaignWeekPeriod = 'pre_period' | 'in_period' | 'post_period';

export type WeekEnding = {week_ending: string; present: boolean; period: CampaignWeekPeriod};

export type WeekInfo = {
  has_error: boolean;
  week_ending_days_of_week: number[];
  error_message: string;
  // null when `has_error` is set, e.g. a locked campaign whose sales data was removed
  week_endings: WeekEnding[] | null;
};

export type SalesLiftResponseVariable = 'sales_dollars' | 'sales_units' | 'eq';

export type SalesLiftAnalysisGroupingType = 'item_attribute' | 'store_attribute' | 'area_attribute' | 'other';

export type CampaignTaskProgressInfo = {
  done?: number;
  total?: number;
  detail?: string;
};

export type CampaignTaskStatusObj = {
  state: string;
  info?: CampaignTaskProgressInfo;
};
// FIXME: make the pending status consistent
export type CampaignTaskStatus = 'COMPLETED' | 'IN_PROGRESS' | 'FAILED' | 'TERMINATED' | 'PROGRESS' | 'PENDING';
export interface CampaignTask {
  id?: string;
  data?: any;
  debug_data?: any;
  updated_at?: string;
  code: string;
  status?: CampaignTaskStatus | null;
  task_id: string;
  taskStatus?: CampaignTaskStatusObj;
}

export type LLMRequestStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface LLMRequest {
  id: number;
  request_type: string;
  status: LLMRequestStatus;
  result?: string | null;
  error_message?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CampaignTestControlSelection {
  id?: string;
  campaign?: Campaign;
  notes?: string;
  sales_data?: any;
  test_stores_count?: number;
  control_stores_count?: number;
  potential_control_stores_count?: number;
  do_not_include_stores_count?: number;
  test_areas_count?: number;
  control_areas_count?: number;
  potential_control_areas_count?: number;
  do_not_include_areas_count?: number;
  zip3_map_data?: {
    control: string[];
    test: string[];
    'potential control': string[];
    'do not include': string[];
  };
  average_percent_difference?: number;
  is_selection_from_uploaded_file?: boolean;
}

export type TestControlEventType = 'file_upload' | 'preset_apply' | 'bulk_edit' | 'selection_run';

export interface CampaignTestControlEvent {
  id: number;
  campaign: number;
  event_type: TestControlEventType;
  note: string;
  metadata: Record<string, any>;
  created_by: number | null;
  created_by_email: string | null;
  created_at: string;
}

export type SelectOption<T> = {
  value: T;
  label: string;
};

export enum LaunchModulePermissionCodes {
  view_launch = 'auth.can_view_launch',
  view_campaign_form = 'auth.can_view_campaign_form',
  create_new_campaign = 'auth.can_create_new_campaign',
  set_main_campaign = 'auth.can_set_main_campaign',
  save_update_displayed_fields_for_campaign = 'auth.can_save_update_displayed_fields_for_campaign',
  search_for_campaigns = 'auth.can_search_for_campaigns',
  view_sales_dataset = 'auth.can_view_sales_dataset',
  save_active_dataset = 'auth.can_save_active_dataset',
  save_own_sales_dataset = 'auth.can_save_own_sales_dataset',
  view_measurement_plan = 'auth.can_view_measurement_plan',
  save_store_attributes = 'auth.can_save_store_attributes',
  save_item_attributes = 'auth.can_save_item_attributes',
  link_associated_campaigns = 'auth.can_link_associated_campaigns',
  create_associated_campaigns = 'auth.can_create_associated_campaigns',
  delete_associated_campaigns = 'auth.can_delete_associated_campaigns',
  save_associated_campaigns = 'auth.can_save_associated_campaigns',
  view_deliverables = 'auth.can_view_deliverables',
  save_deliverables = 'auth.can_save_deliverables',
  view_campaign_image_picker = 'auth.can_view_campaign_image_picker',
  view_campaign_settings = 'auth.can_view_campaign_settings',
  choose_file_to_upload_and_save = 'auth.can_choose_file_to_upload_and_save',
  choose_template_to_use_for_exporting_reports = 'auth.can_choose_template_to_use_for_exporting_reports',
  view_user_access = 'auth.can_view_user_access',
  add_users = 'auth.can_add_users',
  delete_users = 'auth.can_delete_users',
  view_intake_form = 'auth.can_view_campaign_intake_form',
  transfer_campaign_ownership = 'auth.can_transfer_campaign_ownership',
  create_hubspot_deal = 'auth.can_create_hubspot_deal',
  update_hubspot_deal = 'auth.can_update_hubspot_deal',
  check_hubspot_properties = 'auth.can_check_hubspot_properties',
  see_first_available_in_campaign_report = 'auth.can_see_first_available_in_campaign_report',
}

export enum DataManagementModulePermissionCodes {
  view_data_management = 'auth.can_view_data_management',
  view_upload_sales_data = 'auth.can_view_upload_sales_data',
  drop_files_into_upload_section = 'auth.can_drop_files_into_upload_section',
  select_file_adapter = 'auth.can_select_file_adapter',
  process_files = 'auth.can_process_files',
  remove_non_processed_files = 'auth.can_remove_non_processed_files',
  view_upload_pre_sales_data = 'auth.can_view_upload_pre_sales_data',
  process_pre_sales_files = 'auth.can_process_pre_sales_files',
  view_upload_rfp_data = 'auth.can_view_upload_rfp_data',
  process_rfp_files = 'auth.can_process_rfp_files',
  view_upload_media_data = 'auth.can_view_upload_media_data',
  process_media_files = 'auth.can_process_media_files',
  view_replica_state = 'auth.can_view_replica_state',
  view_file_prepare_logs = 'auth.can_view_file_prepare_logs',
  mark_pull_and_load_historic_data_task_completed = 'auth.can_mark_pull_and_load_historic_data_task_completed',
}

export enum ConfigureModulePermissionCodes {
  view_configure = 'auth.can_view_configure',
  view_items = 'auth.can_view_items',
  use_filter_section = 'auth.can_use_filter_section',
  see_quicksight_section = 'auth.can_see_quicksight_section',
  edit_layout_of_main_table = 'auth.can_edit_layout_of_main_table',
  bulk_edit_items = 'auth.can_bulk_edit_items',
  bulk_edit_item_attributes = 'auth.can_bulk_edit_item_attributes',
  download_item_attributes_file = 'auth.can_download_item_attributes_file',
  upload_item_attributes_file = 'auth.can_upload_item_attributes_file',
  compute_sales = 'auth.can_compute_sales',
  sync_campaign_entities_from_sales_dataset = 'auth.can_sync_campaign_entities_from_sales_dataset',
  seed_item_groups = 'auth.can_seed_item_groups',
  view_areas_stores = 'auth.can_view_areas_stores',
  bulk_edit_areas_stores = 'auth.can_bulk_edit_areas_stores',
  bulk_edit_areas_stores_attributes = 'auth.can_bulk_edit_areas_stores_attributes',
  select_test_control = 'auth.can_select_test_control',
  run_analysis = 'auth.can_run_analysis',
  compute_area_store_rollups = 'auth.can_compute_area_store_rollups',
  download_area_store_file = 'auth.can_download_area_store_file',
  upload_area_store_file = 'auth.can_upload_area_store_file',
  seed_location_test_control = 'auth.can_seed_location_test_control',
  view_map = 'auth.can_view_map',
  select_different_test_control_selection_in_table = 'auth.can_select_different_test_control_selection_in_table',
  export_map_selection = 'auth.can_export_map_selection',
  download_test_control_stores = 'auth.can_download_test_control_stores',
  view_client_form = 'auth.can_view_campaign_intake_form',
  apply_location_configuration_preset = 'auth.can_apply_location_configuration_preset',
  view_campaign_test_control_events = 'auth.can_view_campaign_test_control_events',
  view_test_control_findings = 'auth.can_view_test_control_findings',
}

export enum MeasureModulePermissionCodes {
  view_measure = 'auth.can_view_measure',
  view_sales_report = 'auth.can_view_sales_report',
  view_pre_sales_report = 'auth.can_view_pre_sales_report',
  export_pre_sales_report = 'auth.can_export_pre_sales_report',
  view_sales_lift = 'auth.can_view_sales_lift',
  export_end_campaign_report = 'auth.can_export_end_campaign_report',
  export_mid_campaign_report = 'auth.can_export_mid_campaign_report',
  export_multi_variate_report = 'auth.can_export_multi_variate_report',
  export_store_optimization_report = 'auth.can_export_store_optimization_report',
  export_multi_campaign_end_campaign_report = 'auth.can_export_multi_campaign_end_campaign_report',
  view_rfp = 'auth.can_view_rfp',
}

/**
 * Any of these opens the PowerPoint export modal. The Multi Campaign EoC tab also needs
 * export_end_campaign_report, so it is covered.
 */
export const POWER_POINT_EXPORT_PERMISSION_CODES = [
  MeasureModulePermissionCodes.export_end_campaign_report,
  MeasureModulePermissionCodes.export_mid_campaign_report,
  MeasureModulePermissionCodes.export_multi_variate_report,
  MeasureModulePermissionCodes.export_store_optimization_report,
];

export enum HomeModulePermissionCodes {
  view_home = 'auth.can_view_home',
  view_monday_project_tracker = 'auth.can_view_monday_project_tracker',
  view_multi_campaign_dashboard = 'auth.can_view_multi_campaign_dashboard',
}

export enum InventoryModulePermissionCodes {
  view_inventory = 'auth.can_view_inventory',
}

export enum DigitalShelfModulePermissionCodes {
  view_digital_shelf = 'auth.can_view_digital_shelf',
}

export enum ClientPortalModulePermissionCodes {
  view_client_profile = 'auth.can_view_client_profile',
  view_client_business_details = 'auth.can_view_client_business_details',
  edit_client_business_details = 'auth.can_edit_client_business_details',
  view_client_invoices = 'auth.can_view_client_invoices',
  // Lets a staff user look at clients other than their own.
  view_any_client_profile = 'auth.can_view_any_client_profile',
}

export enum InternalToolsModulePermissionCodes {
  view_internal_tools = 'auth.can_view_internal_tools',
  view_tasks = 'auth.can_view_tasks',
  view_logs = 'auth.can_view_logs',
  view_revisions = 'auth.can_view_revisions',
  view_batch_analysis = 'auth.can_view_batch_analysis',
  run_batch_analysis_periods = 'auth.can_run_batch_analysis_periods',
  apply_batch_to_analysis_periods = 'auth.can_apply_batch_to_analysis_periods',
  run_batch_analysis_control_resampling = 'auth.can_run_batch_analysis_control_resampling',
  apply_batch_to_analysis_control_resampling = 'auth.can_apply_batch_to_analysis_control_resampling',
  view_statistical_significance = 'auth.can_view_statistical_significance',
  approve_statistical_significance_for_inclusion_in_measure_section = 'auth.can_approve_statistical_significance_for_inclusion_in_measure_section',
  view_analysis_appendix = 'auth.can_view_analysis_appendix',
  view_audit_log = 'auth.can_view_audit_log',
  view_all_analysis_runs = 'auth.can_view_all_analysis_runs',
  view_campaign_benchmark_data = 'auth.can_view_campaign_benchmark_data',
  create_campaign_benchmark_entry = 'auth.can_create_campaign_benchmark_entry',
  view_brands = 'auth.can_view_brands',
  add_brand = 'auth.can_add_brand',
  change_brand = 'auth.can_change_brand',
  delete_brand = 'auth.can_delete_brand',
  view_category_page = 'auth.can_view_category_page',
  create_category = 'auth.can_create_category',
  edit_category = 'auth.can_edit_category',
  delete_category = 'auth.can_delete_category',
  view_clean_item_list = 'auth.can_view_clean_item_list',
  view_email_generator = 'auth.can_view_email_generator',
  view_hubspot_differences = 'auth.can_view_hubspot_differences',
  lock_campaign = 'auth.can_lock_campaign',
  unlock_campaign = 'auth.can_unlock_campaign',
  view_analysis_run_ai_key_findings = 'auth.can_view_analysis_run_ai_key_findings',
  regenerate_analysis_run_ai_key_findings = 'auth.can_regenerate_analysis_run_ai_key_findings',
  revert_campaign_to_analysis_run = 'auth.can_revert_campaign_to_analysis_run',
  view_rfp_feasibility_internal = 'auth.can_view_rfp_feasibility_internal',
}

export enum CampaignLockChoices {
  DETAILS_LOCKED = 'details_locked',
  ITEMS_LOCKED = 'items_locked',
  LOCATIONS_LOCKED = 'locations_locked',
}

// Campaign Benchmark Data enums (mirror backend TextChoices)
export enum CampaignBenchmarkResultType {
  NOT_AVAILABLE = 'not_available',
  TOPLINE = 'topline',
  RETAILER = 'retailer',
  ITEM = 'item',
}
export enum CampaignBenchmarkMethodNeedToTransf {
  NOT_AVAILABLE = 'Not Available',
  BOTH = 'Both',
  PRE_POST = 'Pre/Post',
  TBD_RECOMMENDATION = 'TBD/Recommendation',
  TEST_CONTROL = 'Test/Control',
  TIGHT_TEST_AND_CONTROL = 'Tight Test and Control',
  NEW_ITEM = 'New Item',
  PANEL = 'Panel',
  SALES_LIFT_ANALYSIS_SAME_WEEKS_AS_LAST_YEAR = 'Sales Lift Analysis - Same Weeks as Last Year',
}
export enum CampaignBenchmarkMethod {
  NOT_AVAILABLE = 'Not Available',
  TEST_CONTROL = 'Test Control',
  WORKING_ON_IT = 'Working on it',
  PRE_POST = 'Pre Post',
}
export enum CampaignBenchmarkDataSource {
  NOT_AVAILABLE = 'Not Available',
  NIELSEN = 'Nielsen',
  NO_NIELSEN = 'No Nielsen',
  MIXED = 'Mixed',
  DONE = 'Done',
  WORKING_ON_IT = 'Working on it',
  STUCK = 'Stuck',
}
export enum CampaignServiceChoices {
  SALES_LIFT_MEASUREMENT = 'Sales Lift Measurement',
  AMAZON_MEASUREMENT = 'Amazon Measurement',
  PRE_SALES_INSIGHTS = 'Pre Sales Insights',
  OTHER = 'Other',
}

export type TabDefinition = {
  tabName: string;
  requiredAnyPermission: ReadonlyArray<string>;
  tabTitle?: string;
  isDisabled?: boolean;
  requireStaff?: boolean;
};

export enum WeeklyMetricsName {
  upc_weekly_average_sales_per_store = 'upc_weekly_average_sales_per_store',
  upc_weekly_average_sales_per_test_store = 'upc_weekly_average_sales_per_test_store',
  upc_weekly_average_sales_per_control_store = 'upc_weekly_average_sales_per_control_store',
  upc_weekly_total_sales_all_stores = 'upc_weekly_total_sales_all_stores',
  upc_weekly_total_sales_test_stores = 'upc_weekly_total_sales_test_stores',
  upc_weekly_total_sales_control_stores = 'upc_weekly_total_sales_control_stores',
  upc_weekly_num_stores_selling = 'upc_weekly_num_stores_selling',
  upc_weekly_num_test_stores_selling = 'upc_weekly_num_test_stores_selling',
  upc_weekly_num_control_stores_selling = 'upc_weekly_num_control_stores_selling',
  area_weekly_average_sales_per_store_hero = 'area_weekly_average_sales_per_store_hero',
  area_weekly_average_sales_per_store_halo = 'area_weekly_average_sales_per_store_halo',
  area_weekly_average_sales_per_store_total = 'area_weekly_average_sales_per_store_total',
  area_weekly_total_sales_hero = 'area_weekly_total_sales_hero',
  area_weekly_total_sales_halo = 'area_weekly_total_sales_halo',
  area_weekly_total_sales_total = 'area_weekly_total_sales_total',
  area_weekly_num_stores_selling_hero = 'area_weekly_num_stores_selling_hero',
  area_weekly_num_stores_selling_halo = 'area_weekly_num_stores_selling_halo',
  area_weekly_num_stores_selling_total = 'area_weekly_num_stores_selling_total',
}

export enum WeeklyMetricsGroups {
  upc_weekly_average_sales = 'upc_weekly_average_sales',
  upc_weekly_total_sales = 'upc_weekly_total_sales',
  upc_weekly_num_stores_selling = 'upc_weekly_num_stores_selling',
  area_weekly_average_sales = 'area_weekly_average_sales',
  area_weekly_total_sales = 'area_weekly_total_sales',
  area_weekly_num_stores_selling = 'area_weekly_num_stores_selling',
}

export interface Metric {
  campaign_id: number;
  label: string;
  group: string;
  name: string;
  value: number;
}

export interface WeeklyMetrics extends Metric {
  week_ending: string | Date;
}

export type WeeklyMetricsParams = {
  campaign_id: string | number;
  label: string;
  group__in?: string;
  week_ending?: string | Date;
  limit?: number;
};

export enum PreSalesReportPeriodType {
  latest_4_wks = 'latest_4_wks',
  latest_13_wks = 'latest_13_wks',
  latest_52_wks = 'latest_52_wks',
}

export interface PreSalesLatest52WksBrandShare {
  brand: string;
  brand_share_pct: number;
}

export interface PreSalesBrandRank {
  brand: string;
  brand_share_pct: number;
  brand_share_pct_ya: number;
  rank: number;
}

export interface PreSalesXaocRegionRank {
  area_id: number;
  area_name: string;
  rank: number;
}

export interface PreSalesRetailerRegionRank {
  area_id: number;
  area_name: string;
  rank: number;
}

export interface PreSalesYoYChange {
  period_type: PreSalesReportPeriodType;
  pct_change_yoy: number;
}

export interface PreSalesWeeklySalesTrend {
  week_ending: string;
  sales_ly: number;
  sales_ty: number;
}

export interface PreSalesBdiCdiEntry {
  area_id: number;
  area_name: string;
  bdi: number; // Brand Development Index
  cdi: number; // Category Development Index
}

export interface PreSalesReportData {
  latest_52wk_brand_share: PreSalesLatest52WksBrandShare[] | null;
  brand_rank: PreSalesBrandRank[] | null;
  top_bottom_xaoc_regions: PreSalesXaocRegionRank[] | null;
  top_retailer_regions: PreSalesRetailerRegionRank[] | null;
  brand_performance: PreSalesYoYChange[] | null;
  category_performance: PreSalesYoYChange[] | null;
  weekly_sales_trend: PreSalesWeeklySalesTrend[] | null;
  bdi_cdi: PreSalesBdiCdiEntry[] | null;
}

export interface PreSalesFiltersArea {
  id: number;
  name: string;
}

export interface PreSalesFiltersResponse {
  brand?: string[];
  category?: string[];
  period_type?: string[];
  area?: PreSalesFiltersArea[];
  suggested_default_brand?: string;
}

export type CommonChartJSOptions = {
  animation?: boolean;
  devicePixelRatio?: number;
};

export enum LandingPageChoices {
  DEFAULT = 'Default',
  LANDING_PAGE = 'LandingPage',
}

export interface AuditLogActor extends Identity {
  display_name: string;
}

export interface AuditLogObjectType extends Identity {
  model: string;
}

export interface CampaignAnalysisWithRunInfo extends Identity {
  campaign_analysis_run_id: number;
  campaign_analysis_run_index: number;
  campaign_analysis_run_created: string;
  campaign_analysis_run_start_date: string;
  campaign_analysis_run_end_date: string;
  campaign_analysis_run_pre_period_start_date: string;
  campaign_analysis_run_pre_period_end_date: string;
  campaign_analysis_run_num_post_campaign_weeks: number;
  campaign_analysis_run_num_control_locations: number;
  item_group: string;
  count: number;
  sales_dollars: number;
  preperiod_sales: number;
  expected_sales: number;
  incremental_sales: number;
  lift: number;
  group: string;
  grouping: string;
  grouping_name: string;
  remove_outliers: boolean;
  compare_to: 'all' | 'group';
  zero_negative_sales: boolean;
  response_variable: SalesLiftResponseVariable;
  stat_sig_p_value: number;
  stat_sig_std_err: number;
  stat_sig_conf_int_lower: number;
  stat_sig_conf_int_upper: number;
}

export interface AnalysisReportSalesLiftResultBase {
  lift_percent: number | null;
  incremental: number;
  positive_incremental: number;
  test_campaign_total: number;
  expected_sales: number;
  // Period totals scaled back up to the configured number of weeks. Null/absent unless outlier
  // weeks were excluded from the analysis.
  extrapolated_incremental?: number | null;
  extrapolated_positive_incremental?: number | null;
  extrapolated_expected_sales?: number | null;
  extrapolated_test_campaign_total?: number | null;
}

export interface AnalysisReportTopLineResultPayload extends AnalysisReportSalesLiftResultBase {
  statistical_significance: {p_value: number | null} | null;
  // Live-budget ROAS figures computed by the backend (dollars-vs-units aware). Extrapolated
  // variants are null unless outlier weeks were excluded.
  roas?: number | null;
  positive_roas?: number | null;
  extrapolated_roas?: number | null;
  extrapolated_positive_roas?: number | null;
}

export interface AnalysisReportItemResult extends AnalysisReportSalesLiftResultBase {
  upc: string;
  item_description: string;
  sales_rank: number;
  incremental_rank: number;
  campaign_item_group: string;
  percentage_of_total_positive_incremental: number | null;
}

export interface AnalysisReportRegionResult extends AnalysisReportSalesLiftResultBase {
  name: string;
  percentage_of_total_positive_incremental: number | null;
}

export interface AnalysisReportVariableInsightValueResult extends AnalysisReportSalesLiftResultBase {
  value: string;
  sales_rank: number;
  incremental_rank: number;
  campaign_item_group: string;
  percentage_of_total_positive_incremental: number | null;
}

export type AnalysisReportVariableInsightType = 'item_attribute' | 'store_attribute' | 'area_attribute' | 'other';

export interface AnalysisReportVariableInsight {
  variable_name: string;
  variable_type: AnalysisReportVariableInsightType;
  values: AnalysisReportVariableInsightValueResult[];
}

export interface AnalysisReportTopLinePerStoreRow {
  test_control: 'test' | 'control';
  preperiod_sales_per_store: number | null;
  sales_dollars_per_store: number | null;
  expected_sales_per_store: number | null;
}

export interface AnalysisReportSalesLiftResults {
  overall: AnalysisReportTopLineResultPayload;
  topline_per_store: AnalysisReportTopLinePerStoreRow[];
  items: AnalysisReportItemResult[];
  regions: AnalysisReportRegionResult[];
  per_region: AnalysisReportRegionResult[];
  variable_insights: AnalysisReportVariableInsight[];
}

export interface CampaignAnalysisRunResultsRow extends Identity {
  campaign_analysis_run: number;
  item_group: string;
  response_variable: SalesLiftResponseVariable;
  zero_negative_sales: boolean;
  remove_outliers: boolean;
  results: AnalysisReportSalesLiftResults;
  statistical_significance: number | string | null;
  created_at: string;
  updated_at: string;
}

export interface WeeklyCampaignAnalysisRunResultsRow extends CampaignAnalysisRunResultsRow {
  week_ending: string;
}

export interface CampaignAnalysisStatisticalSignificance {
  model_name: string;
  model_configuration: Record<string, any>;
  lift: number | null;
  coefficients: Record<string, number>;
  standard_errors: Record<string, number>;
  t_values: Record<string, number>;
  use_t: boolean;
  p_values: Record<string, number>;
  conf_int_lower: Record<string, number>;
  conf_int_upper: Record<string, number>;
  rsquared: number | null;
  result_summary_csv: string;
  errors: string[];
  interaction_coefficient_name: string | null;
  campaign_analysis: number | string | null;
}

export interface CampaignAnalysisRun extends Identity {
  campaign_id: number;
  index: number;
  publishable: boolean;
  start_date: string;
  end_date: string;
  pre_period_start_date: string;
  pre_period_end_date: string;
  num_post_campaign_weeks: number;
  excluded_weeks: string[];
  run_campaign_item_variables: string[];
  run_campaign_location_variables: string[];
  run_system_item_variables: string[];
  run_system_location_variables: string[];
  default_response_variable: string;
  default_map_zip_mode: string;
  default_map_zoom_mode: string;
  default_with_negative_sales_dollars_set_to_zero: boolean;
  is_selected: boolean;
  notes: string;
  key_findings: string;
  created_at: string;
  updated_at: string;
  active_llm_request_id?: number | null;
  latest_publishable?: boolean;
  num_control?: number;
  combined_lift_pct?: number;
  combined_lift_p_value?: number;
  combined_incremental_sales?: number;
  hero_lift_pct?: number;
  hero_lift_p_value?: number;
  hero_incremental_sales?: number;
  halo_lift_pct?: number;
  halo_lift_p_value?: number;
  halo_incremental_sales?: number;
  campaign_week_ending_start_date?: string;
  campaign_week_ending_end_date?: string;
  campaign_week_ending_post_campaign_end_date?: string;
  campaign_week_ending_pre_period_start_date?: string;
  campaign_week_ending_pre_period_end_date?: string;
  num_weeks_pre_period?: number;
  num_weeks_campaign_period?: number;
}

/** Why a test region was left out of a combination's fit. */
export type MultiVariateUnmeasurableReason =
  'no_pre_period_sales' | 'no_lift' | 'no_population' | 'no_mapped_impressions';

/** A test region (zip3 or area) left out of a combination's fit, with the delivery it received. */
export interface MultiVariateUnmeasurableRegion {
  name: string;
  reason: MultiVariateUnmeasurableReason;
  impressions: number;
}

/** One combination's fit. The floor and exclusion fields are absent on runs made before they were per combination. */
export interface MultiVariateCombinationDiagnostics {
  r_squared: number;
  intercept: number;
  num_regions: number;
  impression_threshold?: number;
  excluded_creatives?: string[];
  unmeasurable_regions?: MultiVariateUnmeasurableRegion[];
}

export interface MultiVariateAnalysisRun extends Identity {
  campaign_id: number;
  sales_lift_analysis_run_id: number;
  report_type: string;
  published: boolean;
  /** The highest per-combination impressions floor, and every creative excluded from at least one combination. */
  impression_threshold: number;
  excluded_creatives: string[];
  /** Keyed "{item_group}|{response_variable}|{zero_negative_sales}|{remove_outliers}", booleans as "True"/"False". */
  model_diagnostics: Record<string, MultiVariateCombinationDiagnostics>;
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface MultiVariateAnalysisRunResult extends Identity {
  creative: string;
  creative_size: string;
  test_market_impressions: number;
  avg_exp_rate: number;
  coef_display: number;
  ci_low: number;
  ci_high: number;
  stable: string;
}

export interface PublishedMultiVariateAnalysisRun extends MultiVariateAnalysisRun {
  results: MultiVariateAnalysisRunResult[];
}

export type DashboardBrand = {
  id?: number;
  name: string;
  image_url?: string;
};

export type DashboardCategory = {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
};

export type SystemMessage = {
  id: number;
  message: string;
  status: 'pending' | 'active' | 'completed' | 'cancelled';
  created_at: string;
  updated_at: string;
};

export type CleanItem = {
  id: number;
  upc: string;
  source_description: string;
  category: string;
  brand: DashboardBrand | null;
  clean_description: string;
};

export type CampaignCleanItem = {
  campaign_item_id: number;
  upc: string;
  item_group: string;
  campaign_item_description: string;
  campaign_item_category: string;
  campaign_item_brand: string;
  clean_item_id: number | null;
  clean_item_description: string | null;
  clean_item_category: string | null;
  clean_item_brand_id: number | null;
  clean_item_brand: string | null;
};
